import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { API_BASE } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { AvailabilityDot, VerifiedBadge, RiskPill } from "@/components/Badges";
import { toast } from "sonner";
import { DoorOpen, Users, Sparkles, ArrowRight, BookOpen, UserPlus } from "lucide-react";
import { useAuthedImage } from "@/lib/files";

export default function Match() {
  const { id } = useParams();
  const nav = useNavigate();
  const [req, setReq] = useState(null);
  const [helpers, setHelpers] = useState([]);
  const [cases, setCases] = useState([]);
  const [queue, setQueue] = useState([]);
  const [immediateHelp, setImmediateHelp] = useState(false);
  const [aiText, setAiText] = useState("");
  const [aiModel, setAiModel] = useState("");
  const [aiStreaming, setAiStreaming] = useState(false);
  const [inviting, setInviting] = useState(null);
  const photoSrc = useAuthedImage(req?.attachments?.[0]);

  useEffect(() => {
    (async () => {
      const { data } = await api.get(`/help-requests/${id}`);
      setReq(data);
      const m = await api.get(`/help-requests/${id}/matches`);
      setHelpers(m.data.helpers);
      setCases(m.data.knowledge_cases);
      if (data.ask_mode !== "people") streamAI(data);
    })().catch(() => toast.error("Could not load request"));
    const t = setInterval(async () => {
      try { const { data } = await api.get(`/help-requests/${id}/queue`); setQueue(data); } catch {}
    }, 5000);
    (async () => {
      try { const { data } = await api.get(`/help-requests/${id}/queue`); setQueue(data); } catch {}
    })();
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const invite = async (helperId) => {
    setInviting(helperId);
    try {
      const { data } = await api.post(`/help-requests/${id}/queue/${helperId}/invite`);
      toast.success("Helper invited — opening room");
      nav(`/room/${data.room_id}`);
    } catch { toast.error("Could not invite"); }
    finally { setInviting(null); }
  };

  const streamAI = (r) => {
    setAiStreaming(true);
    setAiText("");
    const img = r.attachments?.[0];
    const url = `${API_BASE}/rooms/${r.room_id}/ai/stream?prompt=${encodeURIComponent(r.text)}&model=${encodeURIComponent(r.ai_model || "auto")}${img ? `&image=${encodeURIComponent(img)}` : ""}&token=${encodeURIComponent(localStorage.getItem("cling_token") || "")}`;
    const es = new EventSource(url, { withCredentials: true });
    es.onmessage = (ev) => {
      try {
        const d = JSON.parse(ev.data);
        if (d.type === "meta") setAiModel(d.model);
        if (d.type === "delta") setAiText((t) => t + d.content);
        if (d.type === "done") { setAiStreaming(false); es.close(); }
      } catch {}
    };
    es.onerror = () => { setAiStreaming(false); es.close(); };
  };

  if (!req) return <div className="min-h-screen"><Navbar /><div className="p-10 text-center text-muted-foreground">Loading…</div></div>;

  return (
    <div className="min-h-screen warm-grain">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <button onClick={() => nav("/home")} className="text-xs text-muted-foreground hover:text-foreground">← Back</button>
        <div className="mt-4 flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-center gap-2 text-xs font-mono-label">
              <span className="text-[color:hsl(var(--teal))]">{req.category}</span>
              <span>·</span>
              <RiskPill risk={req.risk} />
            </div>
            <h1 className="mt-2 font-display font-bold text-2xl sm:text-3xl max-w-3xl">{req.text}</h1>
            {photoSrc && (
              <img src={photoSrc} alt="attached" className="mt-3 rounded-2xl border max-h-64" data-testid="match-photo" />
            )}
          </div>
          <Button onClick={() => nav(`/room/${req.room_id}`)} className="btn-amber rounded-full h-11 px-5" data-testid="open-room-btn">
            <DoorOpen className="w-4 h-4 mr-1" /> Step inside the Help Room
          </Button>
        </div>

        <div className="grid lg:grid-cols-3 gap-6 mt-8">
          <div className="lg:col-span-2 space-y-6">
            {req.ask_mode !== "people" && (
              <div className="border rounded-3xl bg-card p-6 rise-in">
                <div className="flex items-center gap-2 text-xs font-mono-label text-[color:hsl(var(--teal))]">
                  <Sparkles className="w-3 h-3" /> CLING AI {aiModel && `· ${aiModel}`}
                  {aiStreaming && <span className="ml-2 text-muted-foreground animate-pulse">responding…</span>}
                </div>
                <div className="mt-3 whitespace-pre-wrap text-sm leading-relaxed" data-testid="ai-response-text">
                  {aiText || (aiStreaming ? "…" : "")}
                </div>
              </div>
            )}

            {cases.length > 0 && (
              <div className="border rounded-3xl bg-card p-6">
                <div className="flex items-center gap-2 text-xs font-mono-label text-[color:hsl(var(--amber))]">
                  <BookOpen className="w-3 h-3" /> We've seen something similar before
                </div>
                <div className="mt-4 grid gap-3">
                  {cases.map((c) => (
                    <Link key={c.case_id} to={`/knowledge/${c.case_id}`} className="border rounded-xl p-4 hover:border-[color:hsl(var(--teal))]" data-testid={`case-${c.case_id}`}>
                      <div className="text-xs font-mono-label text-muted-foreground">{c.category}</div>
                      <div className="mt-1 font-medium">{c.title}</div>
                      <div className="mt-1 text-xs text-muted-foreground line-clamp-2">{c.problem}</div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <aside className="space-y-4">
            <div className="border rounded-3xl bg-card p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-xs font-mono-label text-[color:hsl(var(--teal))]">
                  <Users className="w-3 h-3" /> Helper Queue {queue.length > 0 && `(${queue.length})`}
                </div>
                <button
                  onClick={() => setImmediateHelp(v => !v)}
                  className={`text-[10px] font-mono-label rounded-full px-2.5 py-1 border transition ${immediateHelp ? "bg-[color:hsl(var(--amber))] text-[color:hsl(var(--amber-foreground))] border-[color:hsl(var(--amber))]" : "text-muted-foreground hover:text-foreground"}`}
                  data-testid="immediate-help-toggle"
                  aria-pressed={immediateHelp}
                >
                  ⚡ Immediate Help
                </button>
              </div>
              {immediateHelp && (
                <div className="mt-3 rounded-xl border border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5 p-3 text-xs" data-testid="immediate-help-panel">
                  <div className="font-semibold text-[color:hsl(var(--amber))]">Priority attention from an experienced helper.</div>
                  <div className="mt-1 text-muted-foreground">Suggested sessions: <b>$10 · 15 min</b> · <b>$20 · 30 min</b>. Prices shown per helper below. Payments coming soon — enable to preview the flow.</div>
                </div>
              )}
              {queue.length === 0 ? (
                <div className="mt-4 text-sm text-muted-foreground">Notifying relevant helpers. Interested people will appear here for you to choose from.</div>
              ) : (
                <div className="mt-4 space-y-3">
                  {queue.map((q) => (
                    <QueueEntry key={q.queue_id} q={q} requestId={id} immediateHelp={immediateHelp} />
                  ))}
                </div>
              )}
              <Button onClick={() => nav(`/room/${req.room_id}`)} variant="outline" className="w-full mt-4 rounded-full" data-testid="invite-helpers-btn">
                Open Help Room <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>

            {helpers.length > 0 && (
              <div className="border rounded-3xl bg-card p-5">
                <div className="flex items-center gap-2 text-xs font-mono-label text-[color:hsl(var(--teal))]">
                  <Users className="w-3 h-3" /> Recommended helpers
                </div>
                <div className="mt-4 space-y-3">
                  {helpers.slice(0, 4).map((h) => (
                    <div key={h.user_id} className="flex items-start gap-3" data-testid={`helper-${h.user_id}`}>
                      <Avatar className="h-9 w-9 border">
                        <AvatarImage src={h.picture} />
                        <AvatarFallback className="bg-[color:hsl(var(--teal))] text-white text-xs">{h.name?.[0]}</AvatarFallback>
                      </Avatar>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <Link to={`/u/${h.user_id}`} className="font-semibold text-sm truncate hover:underline">{h.name}</Link>
                          <AvailabilityDot status={h.availability} />
                        </div>
                        <div className="text-[11px] text-muted-foreground truncate">{h.bio}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          <VerifiedBadge level={h.verification_level} />
                          <span className="text-[10px] font-mono-label text-muted-foreground">{h.people_helped} helped</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </div>
    </div>
  );
}

function QueueEntry({ q, requestId, immediateHelp }) {
  const [busy, setBusy] = useState(false);
  const [showPay, setShowPay] = useState(false);
  const [minutes, setMinutes] = useState(15);
  const nav = useNavigate();
  const u = q.user || {};
  const price = minutes === 15 ? 10 : minutes === 30 ? 20 : 35;

  const invite = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/help-requests/${requestId}/queue/${u.user_id}/invite`);
      toast.success(`Invited ${u.name}`);
      nav(`/room/${data.room_id}`);
    } catch { toast.error("Could not invite"); }
    finally { setBusy(false); }
  };

  const startPaid = () => setShowPay(true);
  const confirmPaid = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/immediate/authorize", {
        helper_id: u.user_id,
        request_id: requestId,
        rate: price,
        minutes,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start Immediate Help checkout");
      setBusy(false);
    }
  };

  return (
    <div className="p-3 rounded-xl border bg-secondary/40" data-testid={`queue-entry-${u.user_id}`}>
      <div className="flex items-start gap-3">
        <Avatar className="h-10 w-10 border">
          <AvatarImage src={u.picture} />
          <AvatarFallback className="bg-[color:hsl(var(--teal))] text-white text-xs">{u.name?.[0]}</AvatarFallback>
        </Avatar>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <Link to={`/u/${u.user_id}`} className="font-semibold text-sm truncate hover:underline" data-testid={`queue-name-${u.user_id}`}>{u.name}</Link>
            <AvailabilityDot status={u.availability} />
          </div>
          <div className="text-[11px] text-muted-foreground truncate">{u.bio}</div>
          <div className="mt-1 flex flex-wrap gap-1">
            <VerifiedBadge level={u.verification_level} />
            <span className="text-[10px] font-mono-label text-muted-foreground">{u.people_helped || 0} helped</span>
            {u.kudos_count > 0 && <span className="text-[10px] font-mono-label text-[color:hsl(var(--amber))]">🙌 {u.kudos_count}</span>}
          </div>
          {q.matched_skills?.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {q.matched_skills.slice(0, 3).map((s) => (
                <span key={s} className="text-[10px] rounded-full bg-[color:hsl(var(--teal))]/10 text-[color:hsl(var(--teal))] px-2 py-0.5 border border-[color:hsl(var(--teal))]/30">{s}</span>
              ))}
            </div>
          )}
        </div>
      </div>

      {immediateHelp && showPay && (
        <div className="mt-3 rounded-xl border border-[color:hsl(var(--amber))]/40 bg-[color:hsl(var(--amber))]/5 p-3 text-xs" data-testid={`pay-panel-${u.user_id}`}>
          <div className="font-mono-label text-[10px] text-[color:hsl(var(--amber))]">Immediate Help session</div>
          <div className="mt-2 flex gap-1.5">
            {[15, 30, 60].map((m) => (
              <button key={m} onClick={() => setMinutes(m)} className={`rounded-full border px-2.5 py-1 ${minutes===m?"bg-[color:hsl(var(--amber))] text-[color:hsl(var(--amber-foreground))] border-[color:hsl(var(--amber))]":""}`} data-testid={`pay-min-${m}`}>{m} min</button>
            ))}
          </div>
          <div className="mt-2 space-y-0.5 text-[11px]">
            <div className="flex justify-between"><span>Helper rate</span><span>${price}.00</span></div>
            <div className="flex justify-between"><span>CLING fee (10%)</span><span>${(price*0.1).toFixed(2)}</span></div>
            <div className="flex justify-between font-semibold pt-1 border-t"><span>Total</span><span>${(price*1.1).toFixed(2)}</span></div>
          </div>
          <div className="mt-2 text-[10px] text-muted-foreground">Your card is held for the session. Funds are captured only when the session ends. Test card 4242 4242 4242 4242.</div>
          <div className="mt-2 flex gap-2">
            <Button size="sm" variant="ghost" className="h-8 flex-1" onClick={() => setShowPay(false)}>Back</Button>
            <Button size="sm" className="btn-amber h-8 flex-1" onClick={confirmPaid} disabled={busy} data-testid={`pay-confirm-${u.user_id}`}>Confirm</Button>
          </div>
        </div>
      )}

      {!showPay && (
        <div className="mt-3 flex gap-2">
          <Button size="sm" className="btn-teal rounded-full h-8 flex-1" onClick={invite} disabled={busy} data-testid={`invite-${u.user_id}`}>
            <UserPlus className="w-3 h-3 mr-1" /> Invite to Help Room
          </Button>
          {immediateHelp && (
            <Button size="sm" variant="outline" className="rounded-full h-8" onClick={startPaid} data-testid={`immediate-${u.user_id}`}>⚡ Immediate</Button>
          )}
        </div>
      )}
    </div>
  );
}

